/**
 * Cursor codec identical to the façade (`acs_service/store.py`
 * `_encode_cursor`/`_decode_cursor`): base64url of the decimal offset with the
 * padding stripped, invalid cursor → offset 0.
 *
 * The fixture provider uses it so the paging logic in the pages is exercised
 * with real cursor strings and needs no change when the HTTP provider takes
 * over.
 */

function base64UrlEncode(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlDecode(value: string): string {
  const padded =
    value.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (value.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

export function encodeCursor(offset: number | null): string | null {
  if (offset === null) return null;
  return base64UrlEncode(String(offset));
}

export function decodeCursor(cursor: string | null | undefined): number {
  if (!cursor) return 0;
  try {
    const decoded = Number.parseInt(base64UrlDecode(cursor), 10);
    return Number.isFinite(decoded) && decoded >= 0 ? decoded : 0;
  } catch {
    return 0;
  }
}

/** Page over `rows` the way the server pages over Mongo: offset + limit. */
export function paginate<T>(
  rows: readonly T[],
  cursor: string | null | undefined,
  limit: number,
): { items: T[]; next_cursor: string | null } {
  const offset = decodeCursor(cursor);
  const items = rows.slice(offset, offset + limit);
  const next = offset + items.length;
  return {
    items,
    next_cursor: next < rows.length ? encodeCursor(next) : null,
  };
}
